import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const MethodologySection = () => {
  const [activeMethod, setActiveMethod] = useState(0);

  const methodologies = [
    {
      icon: "Users",
      title: "Community-Driven Learning",
      subtitle: "Learn Together, Grow Together",
      description: "Our peer-to-peer learning model creates an environment where students learn from each other, share knowledge, and build lasting professional relationships.",
      features: [
        "Collaborative project teams",
        "Peer code reviews and feedback",
        "Study groups and learning circles",
        "Community-generated content"
      ],
      color: "accent"
    },
    {
      icon: "UserCheck",
      title: "Mentorship Excellence",
      subtitle: "Industry Experts as Your Guides",
      description: "Every student is paired with experienced industry professionals who provide personalized guidance, career advice, and technical expertise.",
      features: [
        "1:1 weekly mentorship sessions",
        "Industry-specific career guidance",
        "Technical skill development",
        "Professional network building"
      ],
      color: "cyber-purple"
    },
    {
      icon: "Wrench",
      title: "Project-Based Curriculum",
      subtitle: "Build Real Solutions",
      description: "Learn by doing with hands-on projects that mirror real-world challenges. Build a portfolio that demonstrates your capabilities to potential employers.",
      features: [
        "Real client project opportunities",
        "Portfolio development support",
        "Industry-standard tools and practices",
        "Agile development methodologies"
      ],
      color: "success"
    },
    {
      icon: "Zap",
      title: "Adaptive Learning Path",
      subtitle: "Personalized for Your Goals",
      description: "AI-powered learning paths that adapt to your pace, learning style, and career objectives. No two journeys are the same.",
      features: [
        "Skill assessment and gap analysis",
        "Personalized curriculum recommendations",
        "Flexible pacing and scheduling",
        "Continuous progress tracking"
      ],
      color: "warning"
    }
  ];

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-success/10 border border-success/20 mb-6">
            <Icon name="Cog" size={16} className="text-success mr-2" />
            <span className="text-sm font-medium text-success">Our Approach</span>
          </div>
          
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
            Revolutionary Learning Methodology
          </h2>
          
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            We've reimagined technology education from the ground up. Our methodology combines the best of community learning, expert mentorship, and practical application.
          </p>
        </div>

        {/* Interactive Methodology Showcase */}
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Method Selection */}
          <div className="space-y-4">
            {methodologies.map((method, index) => (
              <div
                key={index}
                onClick={() => setActiveMethod(index)}
                className={`p-6 rounded-xl border cursor-pointer transition-all duration-300 ${
                  activeMethod === index
                    ? `border-${method.color} bg-${method.color}/5 shadow-glow`
                    : 'border-border bg-card/50 hover:bg-card/80'
                }`}
              >
                <div className="flex items-start space-x-4">
                  <div className={`w-12 h-12 rounded-lg bg-gradient-to-br from-${method.color} to-${method.color} flex items-center justify-center shadow-subtle`}>
                    <Icon name={method.icon} size={20} color="white" strokeWidth={2.5} />
                  </div>
                  
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-foreground mb-1">{method.title}</h3>
                    <p className={`text-sm font-medium text-${method.color} mb-2`}>{method.subtitle}</p>
                    <p className="text-sm text-muted-foreground">{method.description}</p>
                  </div>
                  
                  <Icon 
                    name="ChevronRight" 
                    size={20} 
                    className={`transition-transform duration-300 ${
                      activeMethod === index ? 'rotate-90 text-accent' : 'text-muted-foreground'
                    }`} 
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Active Method Details */}
          <div className="lg:sticky lg:top-8">
            <div className="holographic-card p-8">
              <div className="flex items-center space-x-4 mb-6">
                <div className={`w-16 h-16 rounded-xl bg-gradient-to-br from-${methodologies[activeMethod].color} to-${methodologies[activeMethod].color} flex items-center justify-center shadow-glow`}>
                  <Icon name={methodologies[activeMethod].icon} size={24} color="white" strokeWidth={2.5} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-foreground">{methodologies[activeMethod].title}</h3>
                  <p className={`text-${methodologies[activeMethod].color} font-medium`}>{methodologies[activeMethod].subtitle}</p>
                </div>
              </div>
              
              <p className="text-muted-foreground mb-8 leading-relaxed">
                {methodologies[activeMethod].description}
              </p>
              
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-foreground mb-4">Key Features:</h4>
                {methodologies[activeMethod].features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className={`w-2 h-2 rounded-full bg-${methodologies[activeMethod].color}`}></div>
                    <span className="text-muted-foreground">{feature}</span>
                  </div>
                ))}
              </div>
              
              {/* Success Metric */}
              <div className="mt-8 pt-6 border-t border-border">
                <div className="flex items-center justify-between">
                  {/* <div>
                    <div className="text-2xl font-bold text-foreground">98%</div>
                    <div className="text-sm text-muted-foreground">Student Satisfaction</div>
                  </div> */}
                  {/* <div className="text-right">
                    <div className="text-2xl font-bold text-foreground">4.9/5</div>
                    <div className="text-sm text-muted-foreground">Method Rating</div>
                  </div> */}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Methodology Stats */}
        {/* <div className="mt-20 grid md:grid-cols-4 gap-8">
          <div className="text-center">
            <div className="text-3xl font-bold text-accent mb-2">15:1</div>
            <div className="text-sm text-muted-foreground">Student to Mentor Ratio</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-success mb-2">40+</div>
            <div className="text-sm text-muted-foreground">Hours of Mentorship</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-cyber-purple mb-2">12</div>
            <div className="text-sm text-muted-foreground">Real Projects Built</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-warning mb-2">100%</div>
            <div className="text-sm text-muted-foreground">Hands-on Learning</div>
          </div>
        </div> */}
      </div>
    </section>
  );
};

export default MethodologySection;